import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { DragHandle } from './DragHandle';
import {
  TableRow,
  Stack,
  ListItemText,
  TableCell,
  IconButton,
  Tooltip
} from '@mui/material';
import { useLocation } from 'react-router-dom';

import { Switch } from 'components/Switch';
import styled from 'styled-components';
import {
  ModulesTrainingActivitiesSchemasActivitySchema,
  ModulesTrainingAnalysisSchemasWorkflowDependency,
  Workflow,
  WorkflowDependencyItem,
  WorkflowSchema
} from 'schemas';
import { SelectInput } from 'components/Form';
import DeleteIcon from '@mui/icons-material/Delete';
import { secondsToHms } from 'utils';

interface SortableRowProps {
  id: string;
  row: Workflow;
  index: number;
  activity: ModulesTrainingActivitiesSchemasActivitySchema[];
  dependency: ModulesTrainingAnalysisSchemasWorkflowDependency[];
  handleChangeDependency: (id: string, array: WorkflowDependencyItem[]) => void;
  handleDelete: (id: string) => void;
  rows: Workflow[];
  handleToggleMendatory: (rowId: string, value: boolean) => void;
  setRows: React.Dispatch<React.SetStateAction<WorkflowSchema | undefined>>;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: `1px solid  #ededed`,
  padding: '2px'
}));

const SortableRow: React.FC<SortableRowProps> = ({
  id,
  row,
  index,
  activity,
  dependency,
  handleChangeDependency,
  handleDelete,
  handleToggleMendatory,
  rows
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id });
  const location = useLocation();

  const style = {
    transform: transform ? `translateY(${transform.y}px)` : undefined,
    transition,
    fontSize: '10px'
  };

  const handleChange = (value: string[]) => {
    handleChangeDependency(id, value);
  };
  const activityMap = useMemo(() => {
    let map: {
      [key: string]: ModulesTrainingActivitiesSchemasActivitySchema;
    } = {};
    activity.forEach(i => {
      map[i?.id] = i;
    });
    return map;
  }, [activity]);

  const activityDurations = useMemo(() => {
    const map: { [key: string]: { min: number; max: number } } = {};

    activity.forEach(a => {
      const durations =
        a?.clips?.map(c => {
          const start = parseInt(c.start_time, 10);
          const end = parseInt(c.end_time, 10);
          return end - start;
        }) ?? [];

      map[a?.id] = {
        min: durations.length > 0 ? Math.min(...durations) : 0,
        max: durations.length > 0 ? Math.max(...durations) : 0
      };
    });
    return map;
  }, [activity]);

  return (
    <TableRow ref={setNodeRef} style={style}>
      <StyledTableCell component="th" scope="row" style={{ maxWidth: '200px' }}>
        <Stack direction={'row'} alignItems={'center'}>
          <div
            style={{
              padding: '0px 0px 0px 15px',
              whiteSpace: 'nowrap',
              overflow: 'scroll',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            <Tooltip
              title={`${index + 1}. ${activityMap[row?.activity_id]?.activity}`}
            >
              <ListItemText
                primary={`${index + 1}. ${
                  activityMap[row?.activity_id]?.activity
                }`}
              />
            </Tooltip>
          </div>
        </Stack>
      </StyledTableCell>
      <StyledTableCell align="center" width={'250px'}>
        {index > 0 && (
          <SelectInput
            name=""
            label=""
            multiple={true} // multiple is true, so we need to pass an array to value
            options={[
              // Add the 'none' option here
              ...(rows
                .map((r: any, i: number) => {
                  return {
                    value: r?.id, // Make sure the value is correctly set
                    label: `${i + 1}. ${
                      activityMap[r?.activity_id]?.activity?.length < 20
                        ? activityMap[r?.activity_id]?.activity
                        : activityMap[r?.activity_id]?.activity.slice(0, 20) +
                            '...' || 'Unknown Activity'
                    }`
                  };
                })
                .filter((r: any, i: number) => i !== index) || [])
            ]}
            value={Array.isArray(dependency) ? dependency : [dependency]} // Ensure value is always an array
            placeholder="Select Dependency"
            variant="outlined"
            maxWidth={'250px'}
            onChange={(value: any) => handleChange(value)} // Ensure the selected value is an array
            fixedMenuHeight={true}
          />
        )}
      </StyledTableCell>
      <StyledTableCell align="center">
        <Switch
          checked={row?.mandatory || false}
          onChange={(e: any) => {
            handleToggleMendatory(row?.id, e?.target?.checked);
          }}
        />
      </StyledTableCell>
      <StyledTableCell align="center">
        {secondsToHms(activityDurations[row?.activity_id]?.min || 0)}
      </StyledTableCell>
      <StyledTableCell align="center">
        {secondsToHms(activityDurations[row?.activity_id]?.max || 0)}
      </StyledTableCell>
      <StyledTableCell align="center">
        <IconButton
          aria-label="delete"
          disableRipple
          onClick={() => handleDelete(id)}
        >
          <DeleteIcon />
        </IconButton>
        <IconButton style={{ marginTop: '-12px' }} disableRipple>
          <DragHandle {...attributes} {...listeners} isDragging={true} />
        </IconButton>
      </StyledTableCell>
    </TableRow>
  );
};

export default SortableRow;
